import { faEye } from '@fortawesome/free-solid-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons/faEyeSlash'
import { faSquarePlus } from '@fortawesome/free-solid-svg-icons/faSquarePlus'
import { faClose } from '@fortawesome/free-solid-svg-icons'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { createNewSafeZone, updateSafeZone } from '../../handlers/safeZoneHandler.ts'

import { useSnapshot } from 'valtio'
import { controlState } from '../../store/controlState.ts'
import { dataState } from '../../store/dataState.ts'
import { mapState } from '../../store/mapState.ts'
import { safeZoneState, setSafeZonePolygons, setNewSafeZone } from '../../store/safeZoneState.ts'
import { setDrawerOpen } from '../../store/drawerState.ts'
import { CompleteSafeZone } from './components/CompleteSafeZone.tsx'
import { Instructions } from './components/Instructions.tsx'
import { MapSearchBox } from './components/MapSearchBox.tsx'
import { SafeZoneDrawing } from './components/SafeZoneDrawing.tsx'
import { SafeZoneDrawingOptions } from './components/SafeZoneDrawingOptions.tsx'
import { SafeZoneList } from './components/SafeZoneList.tsx'

export type DrawingOptionType = 'hand' | 'address' | ''

export const SafeZonePanel = (props: { close?: () => void }) => {
    const { close } = props

    const map = (useSnapshot(mapState) as typeof mapState).map
    const showSafeZones = useSnapshot(controlState).showSafeZones
    const safeZoneBeingEdited = (useSnapshot(safeZoneState) as typeof safeZoneState).safeZoneBeingEdited
    const safeZones = (useSnapshot(dataState) as typeof dataState).safeZones
    const view = (useSnapshot(safeZoneState) as typeof safeZoneState).view
    const isMobile = useSnapshot(controlState).isOnMobileDevice
    const iconSize = isMobile ? '2x' : '1x'

    // Drag state for desktop
    const panelRef = React.useRef<HTMLDivElement>(null)
    const [pos, setPos] = React.useState({ x: 0, y: 15 })
    const [dragging, setDragging] = React.useState(false)
    const dragOffset = React.useRef({ x: 0, y: 0 })
    const initialized = React.useRef(false)

    // Set initial position based on 25% of viewport width
    React.useEffect(() => {
        if (!isMobile && !initialized.current) {
            setPos({ x: Math.round(window.innerWidth * 0.25), y: 15 })
            initialized.current = true
        }
    }, [isMobile])

    const onMouseDown = (e: React.MouseEvent) => {
        if (isMobile) return
        setDragging(true)
        dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
    }

    React.useEffect(() => {
        if (!dragging) return
        const onMouseMove = (e: MouseEvent) => {
            setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y })
        }
        const onMouseUp = () => setDragging(false)
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [dragging])

    React.useEffect(() => {
        if (safeZoneBeingEdited) {
            const name = safeZones.find(s => s._id === safeZoneBeingEdited)?.zoneName
            if (name) {
                safeZoneState.editedZoneName = name
            }
        }
    }, [safeZoneBeingEdited])
    const saveNewZone = async () => {
        const zones = await createNewSafeZone(safeZoneState.editedZoneName, safeZoneState.newSafeZoneGeometry)
        safeZoneState.instructions = null
        setNewSafeZone(null)
        setDrawerOpen(false)
        safeZoneState.view = 'list'
        if (zones) {
            setSafeZonePolygons([])
            setTimeout(() => {
                dataState.safeZones = zones
            })
        }
    }

    const saveEditedZone = async () => {
        const zones = await updateSafeZone(safeZoneState.safeZoneBeingEdited, safeZoneState.editedZoneName, safeZoneState.updatedZonePath)
        setDrawerOpen(false)
        safeZoneState.view = 'list'
        safeZoneState.safeZoneBeingEdited = ''
        safeZoneState.instructions = null
        if (zones) {
            setSafeZonePolygons([])
            setTimeout(() => {
                dataState.safeZones = zones
            })
        }
    }

    const save = async () => {
        if (safeZoneBeingEdited) {
            await saveEditedZone()
        } else {
            await saveNewZone()
        }
    }

    const cancel = () => {
        safeZoneState.active = false
        safeZoneState.view = 'list'
        safeZoneState.instructions = null
        setNewSafeZone(null)
        safeZoneState.newSafeZoneGeometry = []
        safeZoneState.safeZoneBeingEdited = ''
        safeZoneState.drawingManager?.setMap(null)
    }

    const newZoneButton = (
        <div className={'tooltip tooltip-left'} data-tip={'Add a safe zone'}
             onClick={() => {
                 safeZoneState.view = 'choose'
             }}>
            <FontAwesomeIcon icon={faSquarePlus} size={iconSize}/>
        </div>
    )
    const buttonSpacing = isMobile ? 'w-[2.5rem]' : 'w-fit text-[1.5em]'

    const showHide = (
        <div className={`${buttonSpacing} tooltip tooltip-left`}
             data-tip={showSafeZones ? 'Hide safe zones' : 'Show safe zones'}
             onClick={() => {
                 controlState.showSafeZones = !showSafeZones
             }}>
            {showSafeZones ? <FontAwesomeIcon icon={faEyeSlash} size={iconSize}/> :
                <FontAwesomeIcon icon={faEye} size={iconSize}/>}
        </div>
    )

    const rowStyle = isMobile ? 'justify-around ' : 'justify-end gap-4'
    const card = (
        <div
            className={`${isMobile ? 'w-[75vw]' : 'w-full'} flex flex-col justify-start items-start gap-1 bg-white p-2`}>
            <div
                onMouseDown={onMouseDown}
                className={`prose flex justify-between items-center w-full bg-white text-primary border-b-2 border-b-secondary ${!isMobile ? 'cursor-grab active:cursor-grabbing' : ''}`}
                style={{ userSelect: 'none' }}
            >
                <div className={'w-[50%] self-center'}><h4 className={'m-0 text-2xl text-primary'}>Safe Zones</h4></div>
                <div className={`w-[50%] flex flex-row items-center ${rowStyle} self-center`}>
                    <div className={buttonSpacing}>
                        {view === 'list' ? <span>{newZoneButton}</span> : null}
                    </div>
                    <div>{showHide}</div>
                    <div className={`${buttonSpacing}`} onClick={() => close ? close() : null}>
                        <FontAwesomeIcon icon={faClose} size={iconSize}/>
                    </div>
                </div>

            </div>

            <Instructions/>

            {view === 'list' ? <SafeZoneList map={map!}/> : null}

            {view === 'choose' ? <SafeZoneDrawingOptions map={map!}/> : null}

            {view === 'drawing' ? <SafeZoneDrawing cancel={cancel}/> : null}

            {view === 'search' ? <MapSearchBox placeholder={'enter an address'}/> : null}

            {view === 'finish' ? <CompleteSafeZone save={save} cancel={cancel}/> : null}
        </div>
    )

    const panelStyle: React.CSSProperties = !isMobile
        ? { position: 'fixed', left: pos.x, top: pos.y, zIndex: 10 }
        : {}

    return (
        <div id={'safe-szone'}
             ref={panelRef}
             className={`${isMobile ? 'fixed z-[10] left-[12.5vw] w-[75vw] top-[15px]' : ''} overflow-auto bg-black/70 flex justify-center items-center`}
             style={panelStyle}
             data-theme={'gibi'}
        >
            <div className={'border-b-2 border-b-accent bg-primary-content'}>
                {card}
            </div>
        </div>
    )
}
